import {
  Alert,
  Box,
  Button,
  Container,
  Menu,
  MenuItem,
  Skeleton,
} from "@mui/material";
import { MouseEvent, PropsWithChildren, useState } from "react";
import DashboardFilter from "../../components/Filter/DashboardFilter";
import Footer from "../../components/Footer/Footer";
import ExportDashboard from "../../components/controls/Modals/ExportDashboard";
import { getDashboardDateRange } from "./dateRanges";
import { DashboardExportFormat, downloadDashboard } from "./dashboardExport";
import styles from "./Dashboard.module.scss";
import { DashboardData, DashboardFilters } from "./types";
import useGetDashboardData from "./useGetDashboardData.hook";

const createInitialFilters = (): DashboardFilters => {
  const [startDate, endDate] = getDashboardDateRange("lastThreeMonths");
  return {
    dateRange: "lastThreeMonths",
    startDate,
    endDate,
    regions: ["ALL"],
    countries: ["ALL"],
    screeningFamily: "ALL",
  };
};

const numberFormatter = new Intl.NumberFormat("en-GB");
const dateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});
const qualityScopeLabels: Record<string, string> = {
  ALL: "Across PV + PTQ and PCO",
  PV_PTQ: "Across PV + PTQ",
  PCO: "Across PCO",
};
const refreshedFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Copenhagen",
  timeZoneName: "short",
});
const weekFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
});
const screeningFamilyLabels: Record<string, string> = {
  PV_PTQ: "PV + PTQ",
  PCO: "PCO",
};
const emptyDashboardData: DashboardData = {
  overview: {
    recordsExtracted: 0,
    aiFlaggedRecords: 0,
    reviewsSubmitted: 0,
    samplePassRate: null,
  },
  recordsExtracted: {
    recordsExtracted: 0,
    recordsNotFlagged: 0,
    aiFlaggedRecords: 0,
    flaggedRecordsQcFail: 0,
    flaggedRecordsQcPass: 0,
    flaggedRecordsAwaitingReview: 0,
  },
  reviewerWork: {
    reviewsSubmitted: 0,
    qcPassEvaluations: 0,
    qcFailEvaluations: 0,
    countriesWithReviews: 0,
    reviewsByCountry: [],
    reviewsSubmittedByWeek: [],
    qcFailRateByWeek: [],
  },
  qualitySampling: {
    completedSamples: 0,
    unevaluatedSamples: 0,
    countriesRepresented: 0,
    samplePassRate: null,
    completedSamplesByCountry: [],
  },
  correctiveActions: {
    qcFailEvaluations: 0,
    notificationsSent: 0,
    acknowledged: 0,
    qcFailEvaluationsByScreening: [],
  },
  dataPeriod: { startDate: "", endDate: "" },
  refreshedAt: null,
  filterOptions: { regions: [], countries: [], screeningFamilies: ["ALL"] },
};

const formatDate = (value: string) =>
  value ? dateFormatter.format(new Date(`${value}T00:00:00`)) : "";

const Show = ({ when, children }: PropsWithChildren<{ when: boolean }>) =>
  when ? <>{children}</> : null;

const dashboardDataFrom = (data?: DashboardData) => data ?? emptyDashboardData;
const dashboardIsVisible = (isError: boolean, data?: DashboardData) =>
  !isError || Boolean(data);
const dashboardIsBusy = (isLoading: boolean, isFetching: boolean) =>
  isLoading || isFetching;
const rateOf = (value: number, total: number): number | null =>
  total ? (value / total) * 100 : null;
const formatRate = (value: number | null) =>
  value === null ? "—" : `${value.toFixed(1)}%`;
const qualityScopeFor = (screeningFamily: string) =>
  qualityScopeLabels[screeningFamily] ?? "Across selected screening queues";

// eslint-disable-next-line complexity
const Dashboard = () => {
  const [draftFilters, setDraftFilters] = useState(createInitialFilters);
  const [filters, setFilters] = useState(draftFilters);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [isPdfExportOpen, setIsPdfExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { data, isLoading, isFetching, isError } = useGetDashboardData(filters);
  const dashboardData = dashboardDataFrom(data);

  const dashboardIsLoading = dashboardIsBusy(isLoading, isFetching);
  const showDashboard = dashboardIsVisible(isError, data);

  const metrics = [
    {
      label: "Records extracted",
      value: numberFormatter.format(dashboardData.overview.recordsExtracted),
      note: "Extracted in the selected period",
      tone: "",
    },
    {
      label: "AI-flagged records",
      value: numberFormatter.format(dashboardData.overview.aiFlaggedRecords),
      note: "Of the records extracted",
      tone: styles.attention,
    },
    {
      label: "Reviews submitted",
      value: numberFormatter.format(dashboardData.overview.reviewsSubmitted),
      note: "Submitted in the selected period",
      tone: "",
    },
    {
      label: "Sample pass rate",
      value: formatRate(dashboardData.overview.samplePassRate),
      note: "Samples created in the selected period",
      tone: styles.positive,
    },
  ];

  const extracted = dashboardData.recordsExtracted;
  const extractedMetrics = [
    {
      label: "Records extracted",
      value: extracted.recordsExtracted,
      rate: null,
      note: "Extracted for review",
      tone: "",
    },
    {
      label: "No flag",
      value: extracted.recordsNotFlagged,
      rate: rateOf(extracted.recordsNotFlagged, extracted.recordsExtracted),
      note: "AI found no risk",
      tone: "",
    },
    {
      label: "AI-flagged records",
      value: extracted.aiFlaggedRecords,
      rate: rateOf(extracted.aiFlaggedRecords, extracted.recordsExtracted),
      note: "Sent for reviewer evaluation",
      tone: styles.attention,
    },
    {
      label: "Awaiting review",
      value: extracted.flaggedRecordsAwaitingReview,
      rate: rateOf(
        extracted.flaggedRecordsAwaitingReview,
        extracted.aiFlaggedRecords,
      ),
      note: "Of the AI-flagged records",
      tone: styles.attention,
    },
  ];
  const outcomeItems = [
    {
      label: "QC Fail",
      value: extracted.flaggedRecordsQcFail,
      description: "Reviewer confirmed the AI flag",
      tone: styles.outcomeConfirmed,
    },
    {
      label: "QC Pass",
      value: extracted.flaggedRecordsQcPass,
      description: "Reviewer did not confirm the AI flag",
      tone: styles.outcomeClear,
    },
    {
      label: "Awaiting review",
      value: extracted.flaggedRecordsAwaitingReview,
      description: "No reviewer decision yet",
      tone: styles.outcomePending,
    },
  ];
  const outcomeRate = (value: number) =>
    formatRate(rateOf(value, extracted.aiFlaggedRecords));

  const qualitySampling = dashboardData.qualitySampling;
  const qualityScope = qualityScopeFor(filters.screeningFamily);
  const qualityMetrics = [
    {
      label: "Completed samples",
      value: numberFormatter.format(qualitySampling.completedSamples),
      note: qualityScope,
      tone: "",
    },
    {
      label: "Unevaluated samples",
      value: numberFormatter.format(qualitySampling.unevaluatedSamples),
      note: "Awaiting reviewer evaluation",
      tone: styles.attention,
    },
    {
      label: "Countries represented",
      value: numberFormatter.format(qualitySampling.countriesRepresented),
      note: "Included in completed samples",
      tone: "",
    },
    {
      label: "Overall sample pass rate",
      value: formatRate(qualitySampling.samplePassRate),
      note: "Across reviewed records in completed samples",
      tone: styles.positive,
    },
  ];
  const largestCountrySampleCount = Math.max(
    ...qualitySampling.completedSamplesByCountry.map(
      (country) => country.completedSamples,
    ),
    0,
  );
  const reviewerWork = dashboardData.reviewerWork;
  const reviewMetrics = [
    {
      label: "Reviews submitted",
      value: numberFormatter.format(reviewerWork.reviewsSubmitted),
      note: "On records extracted at any time",
      tone: "",
    },
    {
      label: "QC Pass",
      value: numberFormatter.format(reviewerWork.qcPassEvaluations),
      note: "Reviews marked pass",
      tone: styles.positive,
    },
    {
      label: "QC Fail",
      value: numberFormatter.format(reviewerWork.qcFailEvaluations),
      note: "Reviews marked fail",
      tone: styles.attention,
    },
    {
      label: "Countries with reviews",
      value: numberFormatter.format(reviewerWork.countriesWithReviews),
      note: "At least one review submitted",
      tone: "",
    },
  ];
  const largestCountryReviewCount = Math.max(
    ...reviewerWork.reviewsByCountry.map((country) => country.reviewsSubmitted),
    0,
  );
  const trendMaximum = Math.max(
    ...reviewerWork.qcFailRateByWeek.flatMap((week) =>
      [week.pvPtq, week.pco].filter((rate): rate is number => rate !== null),
    ),
    1,
  );
  const weeklyReviewMaximum = Math.max(
    ...reviewerWork.reviewsSubmittedByWeek.map((week) => week.pvPtq + week.pco),
    1,
  );
  const correctiveActions = dashboardData.correctiveActions;
  const correctiveMetrics = [
    {
      label: "QC Fail evaluations",
      value: numberFormatter.format(correctiveActions.qcFailEvaluations),
      note: "With corrective-action details",
      tone: styles.attention,
    },
    {
      label: "Notifications sent",
      value: numberFormatter.format(correctiveActions.notificationsSent),
      note: "Narrative creators notified",
      tone: "",
    },
    {
      label: "Acknowledged",
      value: numberFormatter.format(correctiveActions.acknowledged),
      note: "Notifications acknowledged",
      tone: styles.positive,
    },
  ];
  const largestScreeningFailCount = Math.max(
    ...correctiveActions.qcFailEvaluationsByScreening.map(
      (screening) => screening.evaluations,
    ),
    0,
  );

  const openExportMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setExportAnchor(event.currentTarget);
  };

  const openPdfExport = () => {
    setExportAnchor(null);
    setIsPdfExportOpen(true);
  };

  const exportDashboard = async (format: DashboardExportFormat) => {
    setExportAnchor(null);
    if (!data) return;

    setIsExporting(true);
    try {
      await downloadDashboard({ format, data, filters });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box className={styles.container}>
      <Container maxWidth="xl" className={styles.page}>
        <Box component="header" className={styles.pageHeading}>
          <Box>
            <h1 className={styles.title}>
              Text Sentinel: Quality &amp; AI Monitoring Dashboard
            </h1>
            <p className={styles.lede}>
              Monitor extracted records, reviewer work, sampling, and corrective
              actions.
            </p>
          </Box>
          <Button
            className={styles.exportButton}
            variant="outlined"
            onClick={openExportMenu}
            disabled={!data || dashboardIsLoading || isExporting}
            aria-controls={exportAnchor ? "dashboard-export-menu" : undefined}
            aria-haspopup="menu"
            aria-expanded={exportAnchor ? "true" : undefined}
          >
            {isExporting ? "Exporting..." : "Export dashboard"}
          </Button>
          <Menu
            id="dashboard-export-menu"
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => exportDashboard("csv")}>CSV</MenuItem>
            <MenuItem onClick={() => exportDashboard("xlsx")}>Excel</MenuItem>
            <MenuItem onClick={openPdfExport}>PDF</MenuItem>
            <MenuItem onClick={() => exportDashboard("json")}>JSON</MenuItem>
          </Menu>
        </Box>

        <Box className={styles.printContext} aria-label="Applied filters">
          <span>
            <strong>Region:</strong>{" "}
            {filters.regions.includes("ALL")
              ? "All regions"
              : filters.regions.join(", ")}
          </span>
          <span>
            <strong>Country:</strong>{" "}
            {filters.countries.includes("ALL")
              ? "All countries"
              : filters.countries.join(", ")}
          </span>
          <span>
            <strong>Risk area or screening queue:</strong>{" "}
            {screeningFamilyLabels[filters.screeningFamily] ??
              filters.screeningFamily}
          </span>
        </Box>

        <Box className={styles.printHidden}>
          <DashboardFilter
            filter={draftFilters}
            regions={dashboardData.filterOptions.regions}
            countries={dashboardData.filterOptions.countries}
            screeningFamilies={dashboardData.filterOptions.screeningFamilies}
            isApplying={isFetching}
            onChange={setDraftFilters}
            onApply={() => setFilters({ ...draftFilters })}
          />
        </Box>

        <Box
          component="nav"
          className={styles.sectionNav}
          aria-label="Dashboard sections"
        >
          <a href="#overview">Overview</a>
          <a href="#records-extracted">Records Extracted</a>
          <a href="#reviewer-work">Reviewer Work Done</a>
          <a href="#quality-sampling">Quality &amp; Sampling Work Done</a>
          <a href="#corrective-actions">Corrective Actions Work Done</a>
        </Box>

        <Box component="section" id="overview" className={styles.overview}>
          <Box className={styles.sectionHeading}>
            <Box>
              <h2 className={styles.sectionTitle}>Overview</h2>
              <p className={styles.sectionDescription}>
                Key operational and quality indicators for the selected filters.
              </p>
            </Box>
            <Show when={Boolean(data)}>
              <p className={styles.sectionContext}>
                Data period: {formatDate(dashboardData.dataPeriod.startDate)} to{" "}
                {formatDate(dashboardData.dataPeriod.endDate)}
                <br />
                Last refreshed:{" "}
                {dashboardData.refreshedAt
                  ? refreshedFormatter.format(
                      new Date(dashboardData.refreshedAt),
                    )
                  : "Not available"}
              </p>
            </Show>
          </Box>

          <Show when={isError}>
            <Alert severity="error" className={styles.error}>
              Dashboard data could not be loaded. Please try again.
            </Alert>
          </Show>

          <Show when={showDashboard}>
            <Box className={styles.summaryGrid}>
              {metrics.map((metric) => (
                <Box
                  component="article"
                  className={styles.summary}
                  key={metric.label}
                >
                  <p className={styles.summaryLabel}>{metric.label}</p>
                  {dashboardIsLoading ? (
                    <Skeleton className={styles.summarySkeleton} />
                  ) : (
                    <p className={`${styles.summaryValue} ${metric.tone}`}>
                      {metric.value}
                    </p>
                  )}
                  <p className={styles.summaryNote}>{metric.note}</p>
                </Box>
              ))}
            </Box>
          </Show>
        </Box>

        <Show when={showDashboard}>
          <Box
            component="section"
            id="records-extracted"
            className={styles.recordsExtracted}
            aria-busy={dashboardIsLoading}
          >
            <Box className={styles.sectionHeading}>
              <Box>
                <h2 className={styles.sectionTitle}>Records Extracted</h2>
                <p className={styles.sectionDescription}>
                  Records extracted in the selected period, with their review
                  status today.
                </p>
              </Box>
            </Box>

            <Box component="article" className={styles.panel}>
              <Box className={styles.panelHeading}>
                <Box>
                  <h3 className={styles.panelTitle}>Extraction summary</h3>
                  <p className={styles.panelSubtitle}>
                    Records extracted for review and screened by AI.
                  </p>
                </Box>
                <Show when={Boolean(data)}>
                  <span className={styles.caption}>
                    {formatDate(dashboardData.dataPeriod.startDate)} to{" "}
                    {formatDate(dashboardData.dataPeriod.endDate)}
                  </span>
                </Show>
              </Box>

              <Box className={styles.processingMetrics}>
                {extractedMetrics.map((metric) => (
                  <Box className={styles.processingMetric} key={metric.label}>
                    <p className={styles.processingLabel}>{metric.label}</p>
                    {dashboardIsLoading ? (
                      <Skeleton className={styles.processingValueSkeleton} />
                    ) : (
                      <p className={`${styles.processingValue} ${metric.tone}`}>
                        {numberFormatter.format(metric.value)}
                        {metric.rate !== null && (
                          <>
                            {" "}
                            <span>{metric.rate.toFixed(1)}%</span>
                          </>
                        )}
                      </p>
                    )}
                    <p className={styles.processingNote}>{metric.note}</p>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              className={styles.outcomeSequence}
              role="group"
              aria-label="AI and reviewer outcomes for extracted records"
            >
              <Box component="article" className={styles.outcomeStep}>
                <p className={styles.outcomeStepNumber}>Step 1 · AI outcome</p>
                <h3 className={styles.panelTitle}>Screening result</h3>
                <Box className={styles.outcomeOptions}>
                  <span className={styles.outcomeOption}>
                    <strong>
                      No flag ·{" "}
                      {numberFormatter.format(extracted.recordsNotFlagged)}
                    </strong>
                  </span>
                  <span
                    className={`${styles.outcomeOption} ${styles.outcomeConfirmed}`}
                  >
                    <strong>
                      Flagged for review ·{" "}
                      {numberFormatter.format(extracted.aiFlaggedRecords)}
                    </strong>
                  </span>
                </Box>
              </Box>

              <Box className={styles.outcomeArrow} aria-hidden="true">
                →
              </Box>

              <Box component="article" className={styles.outcomeStep}>
                <p className={styles.outcomeStepNumber}>
                  Step 2 · Reviewer outcome
                </p>
                <h3 className={styles.panelTitle}>
                  Review status of AI-flagged records
                </h3>
                <Box className={styles.outcomeOptions}>
                  {outcomeItems.map((outcome) => (
                    <Box
                      className={`${styles.outcomeOption} ${outcome.tone}`}
                      key={outcome.label}
                    >
                      <strong>
                        {outcome.label} ·{" "}
                        {numberFormatter.format(outcome.value)} (
                        {outcomeRate(outcome.value)})
                      </strong>
                      <span>{outcome.description}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Show>

        <Show when={showDashboard}>
          <Box
            component="section"
            id="reviewer-work"
            className={styles.reviewerWork}
            aria-busy={dashboardIsLoading}
          >
            <Box className={styles.sectionHeading}>
              <Box>
                <h2 className={styles.sectionTitle}>Reviewer Work Done</h2>
                <p className={styles.sectionDescription}>
                  Reviews submitted in the selected period, on records extracted
                  at any time.
                </p>
              </Box>
            </Box>

            <Box className={styles.summaryGrid}>
              {reviewMetrics.map((metric) => (
                <Box
                  component="article"
                  className={styles.summary}
                  key={metric.label}
                >
                  <p className={styles.summaryLabel}>{metric.label}</p>
                  {dashboardIsLoading ? (
                    <Skeleton className={styles.summarySkeleton} />
                  ) : (
                    <p className={`${styles.summaryValue} ${metric.tone}`}>
                      {metric.value}
                    </p>
                  )}
                  <p className={styles.summaryNote}>{metric.note}</p>
                </Box>
              ))}
            </Box>

            <Box className={styles.reviewGrid}>
              <Box component="article" className={styles.panel}>
                <Box className={styles.panelHeading}>
                  <Box>
                    <h3 className={styles.panelTitle}>
                      Reviews submitted by week
                    </h3>
                    <p className={styles.panelSubtitle}>
                      Weekly totals by risk type. A review can cover both risk
                      types, so the bars can add up to more than the total.
                    </p>
                  </Box>
                  <Box className={styles.chartLegend}>
                    <span>
                      <i className={styles.pvLegend} />
                      PV + PTQ
                    </span>
                    <span>
                      <i className={styles.pcoLegend} />
                      PCO
                    </span>
                  </Box>
                </Box>
                {dashboardIsLoading ? (
                  <Skeleton className={styles.chartSkeleton} />
                ) : reviewerWork.reviewsSubmittedByWeek.length ? (
                  <Box className={styles.weeklyBars}>
                    {reviewerWork.reviewsSubmittedByWeek.map((week) => {
                      const total = week.pvPtq + week.pco;
                      return (
                        <Box className={styles.weeklyRow} key={week.weekStart}>
                          <span className={styles.weeklyLabel}>
                            {weekFormatter.format(
                              new Date(`${week.weekStart}T00:00:00`),
                            )}
                          </span>
                          <Box
                            className={styles.stackedBar}
                            role="img"
                            aria-label={`${week.pvPtq} PV and PTQ reviews and ${week.pco} PCO reviews`}
                          >
                            <span
                              className={`${styles.stackSegment} ${styles.pvBar}`}
                              style={{
                                width: `${(week.pvPtq / weeklyReviewMaximum) * 100}%`,
                              }}
                            />
                            <span
                              className={`${styles.stackSegment} ${styles.pcoBar}`}
                              style={{
                                width: `${(week.pco / weeklyReviewMaximum) * 100}%`,
                              }}
                            />
                          </Box>
                          <span className={styles.weeklyTotal}>
                            {numberFormatter.format(total)}
                          </span>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <p className={styles.emptyState}>
                    No reviews submitted match the selected filters.
                  </p>
                )}
              </Box>

              <Box component="article" className={styles.panel}>
                <Box className={styles.panelHeading}>
                  <Box>
                    <h3 className={styles.panelTitle}>QC Fail rate by week</h3>
                    <p className={styles.panelSubtitle}>
                      Share of reviews on AI-flagged records where the reviewer
                      confirmed the flag. Sample reviews are not included.
                    </p>
                  </Box>
                </Box>
                <Box className={styles.chartLegend}>
                  <span>
                    <i className={styles.pvLegend} />
                    PV + PTQ
                  </span>
                  <span>
                    <i className={styles.pcoLegend} />
                    PCO
                  </span>
                </Box>
                {dashboardIsLoading ? (
                  <Skeleton className={styles.chartSkeleton} />
                ) : reviewerWork.qcFailRateByWeek.length ? (
                  <Box className={styles.trendChart}>
                    {reviewerWork.qcFailRateByWeek.map((week) => (
                      <Box className={styles.trendGroup} key={week.weekStart}>
                        <Box className={styles.trendBars}>
                          {week.pvPtq !== null && (
                            <Box
                              className={`${styles.trendBar} ${styles.pvBar}`}
                              style={{
                                height: `${(week.pvPtq / trendMaximum) * 100}%`,
                              }}
                              title={`PV + PTQ: ${week.pvPtq.toFixed(1)}%`}
                            />
                          )}
                          {week.pco !== null && (
                            <Box
                              className={`${styles.trendBar} ${styles.pcoBar}`}
                              style={{
                                height: `${(week.pco / trendMaximum) * 100}%`,
                              }}
                              title={`PCO: ${week.pco.toFixed(1)}%`}
                            />
                          )}
                        </Box>
                        <span className={styles.trendLabel}>
                          {weekFormatter.format(
                            new Date(`${week.weekStart}T00:00:00`),
                          )}
                        </span>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <p className={styles.emptyState}>
                    No reviews of AI-flagged records match the selected filters.
                  </p>
                )}
              </Box>
            </Box>

            <Box
              component="article"
              className={`${styles.panel} ${styles.countryPanel}`}
            >
              <Box className={styles.panelHeading}>
                <Box>
                  <h3 className={styles.panelTitle}>Reviews by country</h3>
                  <p className={styles.panelSubtitle}>
                    Countries with the most reviews submitted.
                  </p>
                </Box>
                <span className={styles.caption}>Top five</span>
              </Box>
              {dashboardIsLoading ? (
                <Skeleton className={styles.chartSkeleton} />
              ) : reviewerWork.reviewsByCountry.length ? (
                <Box className={styles.countryBars}>
                  {reviewerWork.reviewsByCountry.map((country) => (
                    <Box className={styles.countryRow} key={country.country}>
                      <span className={styles.countryName}>
                        {country.country}
                      </span>
                      <Box
                        className={styles.countryBar}
                        role="img"
                        aria-label={`${country.country}, ${numberFormatter.format(country.reviewsSubmitted)} reviews submitted`}
                      >
                        <Box
                          className={styles.countryFill}
                          style={{
                            width: `${
                              largestCountryReviewCount
                                ? (country.reviewsSubmitted /
                                    largestCountryReviewCount) *
                                  100
                                : 0
                            }%`,
                          }}
                        />
                      </Box>
                      <span className={styles.countryCount}>
                        {numberFormatter.format(country.reviewsSubmitted)}
                      </span>
                    </Box>
                  ))}
                </Box>
              ) : (
                <p className={styles.emptyState}>
                  No reviews submitted match the selected filters.
                </p>
              )}
            </Box>
          </Box>
        </Show>

        <Show when={showDashboard}>
          <Box
            component="section"
            id="quality-sampling"
            className={styles.qualitySampling}
            aria-busy={dashboardIsLoading}
          >
            <Box className={styles.sectionHeading}>
              <Box>
                <h2 className={styles.sectionTitle}>
                  Quality &amp; Sampling Work Done
                </h2>
                <p className={styles.sectionDescription}>
                  Samples created in the selected period and their evaluation
                  results.
                </p>
              </Box>
            </Box>

            <Box className={styles.summaryGrid}>
              {qualityMetrics.map((metric) => (
                <Box
                  component="article"
                  className={styles.summary}
                  key={metric.label}
                >
                  <p className={styles.summaryLabel}>{metric.label}</p>
                  {dashboardIsLoading ? (
                    <Skeleton className={styles.summarySkeleton} />
                  ) : (
                    <p className={`${styles.summaryValue} ${metric.tone}`}>
                      {metric.value}
                    </p>
                  )}
                  <p className={styles.summaryNote}>{metric.note}</p>
                </Box>
              ))}
            </Box>

            <Box
              component="article"
              className={`${styles.panel} ${styles.countryPanel}`}
            >
              <Box className={styles.panelHeading}>
                <Box>
                  <h3 className={styles.panelTitle}>
                    Completed samples by country
                  </h3>
                  <p className={styles.panelSubtitle}>
                    Countries ranked by completed sample count.
                  </p>
                </Box>
                <span className={styles.caption}>Top five</span>
              </Box>

              {dashboardIsLoading ? (
                <Box className={styles.countryBars}>
                  {[0, 1, 2].map((item) => (
                    <Skeleton className={styles.countrySkeleton} key={item} />
                  ))}
                </Box>
              ) : qualitySampling.completedSamplesByCountry.length ? (
                <Box className={styles.countryBars}>
                  {qualitySampling.completedSamplesByCountry.map((country) => (
                    <Box className={styles.countryRow} key={country.country}>
                      <span className={styles.countryName}>
                        {country.country}
                      </span>
                      <Box
                        className={styles.countryBar}
                        role="img"
                        aria-label={`${country.country}, ${numberFormatter.format(
                          country.completedSamples,
                        )} completed samples`}
                      >
                        <Box
                          className={styles.countryFill}
                          style={{
                            width: `${
                              largestCountrySampleCount
                                ? (country.completedSamples /
                                    largestCountrySampleCount) *
                                  100
                                : 0
                            }%`,
                          }}
                        />
                      </Box>
                      <span className={styles.countryCount}>
                        {numberFormatter.format(country.completedSamples)}
                      </span>
                    </Box>
                  ))}
                </Box>
              ) : (
                <p className={styles.emptyState}>
                  No completed samples match the selected filters.
                </p>
              )}
            </Box>
          </Box>
        </Show>
        <Show when={showDashboard}>
          <Box
            component="section"
            id="corrective-actions"
            className={styles.correctiveActions}
            aria-busy={dashboardIsLoading}
          >
            <Box className={styles.sectionHeading}>
              <Box>
                <h2 className={styles.sectionTitle}>
                  Corrective Actions Work Done
                </h2>
                <p className={styles.sectionDescription}>
                  QC Fail reviews submitted in the selected period and the
                  notifications they triggered.
                </p>
              </Box>
            </Box>

            <Box className={`${styles.summaryGrid} ${styles.summaryGridThree}`}>
              {correctiveMetrics.map((metric) => (
                <Box
                  component="article"
                  className={styles.summary}
                  key={metric.label}
                >
                  <p className={styles.summaryLabel}>{metric.label}</p>
                  {dashboardIsLoading ? (
                    <Skeleton className={styles.summarySkeleton} />
                  ) : (
                    <p className={`${styles.summaryValue} ${metric.tone}`}>
                      {metric.value}
                    </p>
                  )}
                  <p className={styles.summaryNote}>{metric.note}</p>
                </Box>
              ))}
            </Box>

            <Box
              component="article"
              className={`${styles.panel} ${styles.countryPanel}`}
            >
              <Box className={styles.panelHeading}>
                <Box>
                  <h3 className={styles.panelTitle}>
                    QC Fail evaluations by screening
                  </h3>
                  <p className={styles.panelSubtitle}>
                    Grouped by the AI screening that raised the flag.
                  </p>
                </Box>
              </Box>
              {dashboardIsLoading ? (
                <Skeleton className={styles.chartSkeleton} />
              ) : correctiveActions.qcFailEvaluationsByScreening.length ? (
                <Box className={styles.riskBars}>
                  {correctiveActions.qcFailEvaluationsByScreening.map(
                    (screening) => (
                      <Box key={screening.screeningFamily}>
                        <Box className={styles.riskBarLabel}>
                          <span>
                            {screeningFamilyLabels[screening.screeningFamily] ??
                              screening.screeningFamily}
                          </span>
                          <span>
                            {numberFormatter.format(screening.evaluations)}{" "}
                            evaluations
                          </span>
                        </Box>
                        <Box className={styles.riskBar}>
                          <Box
                            className={styles.riskFill}
                            style={{
                              width: `${
                                largestScreeningFailCount
                                  ? (screening.evaluations /
                                      largestScreeningFailCount) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </Box>
                      </Box>
                    ),
                  )}
                </Box>
              ) : (
                <p className={styles.emptyState}>
                  No QC Fail evaluations match the selected filters.
                </p>
              )}
            </Box>
          </Box>
        </Show>
      </Container>
      <Box className={styles.printHidden}>
        <Footer isFixed={false} />
      </Box>
      <ExportDashboard
        open={isPdfExportOpen}
        closeModal={() => setIsPdfExportOpen(false)}
        onPrint={() => exportDashboard("pdf")}
      />
    </Box>
  );
};

export default Dashboard;
